import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

function coerceEventTags(value) {
	if (Array.isArray(value)) return value
	if (typeof value === 'string') {
		const t = value.trim()
		if (!t) return []
		try {
			const parsed = JSON.parse(t)
			if (Array.isArray(parsed)) return parsed
		} catch {
			/* ignore */
		}
		return [t]
	}
	return []
}

function normalizeTaskData(data) {
	if (data == null) return data
	if (data.eventTags == null && data.EventTags == null) return data
	const { EventTags, ...rest } = data
	return {
		...rest,
		eventTags: coerceEventTags(data.eventTags ?? EventTags),
	}
}

// Base URL and API version for timeline event-related endpoints
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v1'

// Async thunk to fetch all timeline events from the server
export const getEvents = createAsyncThunk(
	'events/getEvents',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/get-events/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()

			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getVendorEvents = createAsyncThunk(
	'events/getVendorEvents',
	async (isVendorTasks = 'isVendorTasks') => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/get-vendor-events/${isVendorTasks}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()

			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getWeddingsEvents = createAsyncThunk(
	'events/getWeddingsEvents',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/weddings-events`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to create one or more new events
export const createEvents = createAsyncThunk(
	'events/createEvents',
	async ({ events, kazooId }) => {
		try {
			let normalizedEvents = events
			if (events?.EventTags != null) {
				normalizedEvents = {
					...events,
					EventTags: coerceEventTags(events.EventTags),
				}
			}
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/create-events/${kazooId}`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ events: normalizedEvents }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const createTask = createAsyncThunk(
	'events/createTask',
	async ({ kazooId, data }) => {
		try {
			const normalizedData = normalizeTaskData(data)
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/create-task/${kazooId}`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ data: normalizedData }),
				}
			);
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
		}
	}
);

export const createVendorTask = createAsyncThunk(
	'events/createVendorTask',
	async ({ data }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/create-vendor-task`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ data }),
				}
			);
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
		}
	}
);

export const createVendorEvent = createAsyncThunk(
	'events/createVendorEvent',
	async ({ data }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/create-vendor-event`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ data }),
				}
			);
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
		}
	}
);

export const getVendorStaffEvents = createAsyncThunk(
	'events/getVendorStaffEvents',
	async () => {
		try {
			const res = await fetch(`${REQUEST_URL}/${API_VERSION}/events/vendor-staff`, {
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			});
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
		}
	}
);

export const deleteVendorTask = createAsyncThunk(
	'events/deleteVendorTask',
	async ({ id }) => {
		try {
			const res = await fetch(`${REQUEST_URL}/${API_VERSION}/events/delete-vendor-task`, {
				method: 'DELETE',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ id }),
			});
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
			return { success: false, message: err.message };
		}
	}
);

// Async thunk to update a single event
export const updateEvent = createAsyncThunk(
	'events/updateEvent',
	async ({ events, kazooId }) => {
		try {
			const normalizedEvents = Array.isArray(events)
				? events.map((event) =>
						event?.EventTags != null
							? {
									...event,
									EventTags: coerceEventTags(event.EventTags),
								}
							: event
					)
				: events
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/update-event/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ events: normalizedEvents }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const updateTask = createAsyncThunk(
	'events/updateTask',
	async ({ kazooId, id, data }) => {
		try {
			const normalizedData = normalizeTaskData(data)
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/update-task/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ id, data: normalizedData }),
				}
			);
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
			return { success: false, message: err.message };
		}
	}
);

export const updateVendorTask = createAsyncThunk(
	'events/updateVendorTask',
	async ({ id, data }) => {
		try {
			const res = await fetch(`${REQUEST_URL}/${API_VERSION}/events/update-vendor-task`, {
				method: 'PATCH',
				credentials: 'include',
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				body: JSON.stringify({ id, data }),
			});
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
			return { success: false, message: err.message };
		}
	}
);

export const updateVendorEvent = createAsyncThunk(
	'events/updateVendorEvent',
	async ({ id, data }) => {
		try {
			const res = await fetch(`${REQUEST_URL}/${API_VERSION}/events/update-vendor-event`, {
				method: 'PATCH',
				credentials: 'include',
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				body: JSON.stringify({ id, data }),
			});
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
			return { success: false, message: err.message };
		}
	}
);

export const deleteVendorEvent = createAsyncThunk(
	'events/deleteVendorEvent',
	async ({ id }) => {
		try {
			const res = await fetch(`${REQUEST_URL}/${API_VERSION}/events/delete-vendor-event`, {
				method: 'DELETE',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ id }),
			});
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
			return { success: false, message: err.message };
		}
	}
);





// Async thunk to delete one or more events by ID
export const deleteEvents = createAsyncThunk(
	'events/deleteEvents',
	async ({ ids, kazooId }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/delete-events/${kazooId}`,
				{
					method: 'DELETE',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ ids }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const deleteTask = createAsyncThunk(
	'events/deleteTask',
	async ({ kazooId, id }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/events/delete-task/${kazooId}`,
				{
					method: 'DELETE',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ id }),
				}
			);
			const result = await res.json();
			return result;
		} catch (err) {
			console.log(err.message);
			return { success: false, message: err.message };
		}
	}
);

// Initial state of the scheduler slice
const initialState = {
	timelineEvents: [], // List of all timeline events visible in the calendar
	selectedUsers: [], // List of users to display for timeline
	weddingsEvents: [],
	vendorTasks: [],
	vendorEvents: []
}

// Redux slice to manage timeline events (CRUD)
const schedulerSlice = createSlice({
	name: 'scheduler',
	initialState,
	reducers: {
		setSelectedUsers: (state, { payload }) => {
			state.selectedUsers = payload
		},
	},

	extraReducers: (builder) => {
		builder
			// Handle successful fetch of events
			.addCase(getEvents.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					state.timelineEvents = []
					return
				}
				state.timelineEvents = payload.events
			})

			.addCase(getVendorEvents.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to fetch events', 'error')
					if(payload.isVendorTasks) state.vendorTasks = []
					else state.vendorEvents = []
					state.timelineEvents = []
					return
				}
				const events = payload.events?.length > 0 ? payload.events : []
				if(payload.isVendorTasks) state.vendorTasks = events
				else state.vendorEvents = events
				state.timelineEvents = events
			})

			.addCase(getVendorStaffEvents.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					state.timelineEvents = []
					return
				}
				state.timelineEvents = payload.events
			})

			.addCase(getWeddingsEvents.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					state.weddingsEvents = []
					return
				}
				state.weddingsEvents = payload.data
			})

			// Handle successful event creation
			.addCase(createEvents.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to create event',
						'error'
					)
					return
				}

				toastHelper(payload?.message || 'Event created', 'success')
				state.timelineEvents = [
					...state.timelineEvents,
					...payload.events, // Append new events to the current list
				]
			})

			.addCase(createTask.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to create task', 'error');
					return;
				}

				toastHelper(payload?.message || 'Task created', 'success');
				state.timelineEvents = [...state.timelineEvents, payload.task];
			})

			.addCase(createVendorTask.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to create vendor task', 'error');
					return;
				}

				toastHelper(payload?.message || 'Vendor task created', 'success');
				if (payload.events) state.timelineEvents = [...state.timelineEvents, ...payload.events];
				else if (payload.task) state.timelineEvents = [...state.timelineEvents, payload.task];
				// Fallback for singular task if events not present (though we just added it)
			})

			.addCase(createVendorEvent.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to create vendor event', 'error');
					return;
				}

				toastHelper(payload?.message || 'Vendor event created', 'success');
				if (payload?.events) state.timelineEvents = [...state.timelineEvents, ...payload.events];
				else if (payload?.event) state.timelineEvents = [...state.timelineEvents, payload.event];
			})

			// Handle successful event update
			.addCase(updateEvent.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to update event',
						'error'
					)
					return
				}

				state.timelineEvents = state.timelineEvents.filter(
					(event) => !payload.eventIds.includes(event._id)
				)

				state.timelineEvents = [
					...state.timelineEvents,
					...payload.events,
				]
			})

			.addCase(updateTask.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to update task', 'error');
					return;
				}
				const t = payload.task;
				toastHelper(payload?.message || 'Task updated', 'success');

				state.timelineEvents = state.timelineEvents.map((e) => {
					if (String(e._id) !== String(t._id)) return e;
					return {
						...e,
						...t,
					};
				})
			})

			.addCase(updateVendorTask.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to update vendor task', 'error');
					return;
				}
				toastHelper(payload?.message || 'Task updated', 'success');
				// replace the task in timelineEvents
				if (payload.events) {
					state.timelineEvents = state.timelineEvents.filter(
						(e) => e._id !== payload.task._id
					);
					state.timelineEvents = [...state.timelineEvents, ...payload.events];
				} else {
					// Fallback
					const updated = payload.task;
					state.timelineEvents = state.timelineEvents.map(e =>
						e._id === updated._id ? updated : e
					);
				}
			})

			// Handle successful deletion of one or more events
			.addCase(deleteEvents.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to delete event[s]',
						'error'
					)
					return
				}

				toastHelper(payload?.message || 'Event[s] deleted', 'success')
				state.timelineEvents = state.timelineEvents.filter(
					(event) => !payload.eventIds.includes(event._id)
				)
			})

			.addCase(deleteTask.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to delete task', 'error');
					return;
				}
				toastHelper(payload?.message || 'Task deleted', 'success');
				state.timelineEvents = state.timelineEvents.filter(
					(e) => e._id !== payload.taskId
				);
			})

			.addCase(deleteVendorTask.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to delete vendor task', 'error');
					return;
				}
				toastHelper(payload?.message || 'Vendor task deleted', 'success');
				state.timelineEvents = state.timelineEvents.filter(
					(e) => e._id !== payload.taskId
				);
			})

			.addCase(updateVendorEvent.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to update vendor event', 'error');
					return;
				}
				toastHelper(payload?.message || 'Vendor event updated', 'success');
				if (payload.events) {
					state.timelineEvents = state.timelineEvents.filter(
						(e) => e._id !== payload.task._id
					);
					state.timelineEvents = [...state.timelineEvents, ...payload.events];
				} else {
					const updated = payload.task;
					state.timelineEvents = state.timelineEvents.map(e =>
						e._id === updated._id ? updated : e
					);
				}
			})

			.addCase(deleteVendorEvent.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to delete vendor event', 'error');
					return;
				}
				toastHelper(payload?.message || 'Vendor event deleted', 'success');
				state.timelineEvents = state.timelineEvents.filter(
					(e) => e._id !== payload.taskId
				);
			});
	},
})

export const { setSelectedUsers } = schedulerSlice.actions
export default schedulerSlice.reducer
